import { useEffect, useState } from 'react'
import { APP_URL, MARKETING_URL, STATUS_URL } from '@/lib/config'
import { ListIcon, XIcon } from '@phosphor-icons/react'
import { Link, useLocation } from 'react-router-dom'

import { SearchBox } from './SearchBox'
import { SiteLinks } from './SiteLinks'

/**
 * A simpler take on the scrambl.com nav so the help center reads as part of
 * the same site: the floating white pill, the Krona One wordmark (linking
 * back to scrambl.com), a few of its top links, and the same Log in +
 * Start free trial pair. Help center is the current page in the link row.
 */
export function SiteHeader({
  onToggleNav,
  isNavOpen,
}: {
  onToggleNav?: () => void
  isNavOpen?: boolean
}) {
  const location = useLocation()
  const isHome = location.pathname === '/'
  // Pages without the docs drawer (the home page) get their own small menu
  // for the links a narrow header hides.
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  useEffect(() => {
    setIsMenuOpen(false)
  }, [location.pathname])
  const hasDrawer = Boolean(onToggleNav)
  const isOpen = hasDrawer ? Boolean(isNavOpen) : isMenuOpen

  return (
    <header className="site-header">
      <div className="site-header-inner">
        <div className="site-header-left">
          <button
            type="button"
            className="nav-toggle"
            onClick={onToggleNav ?? (() => setIsMenuOpen(open => !open))}
            aria-label={
              hasDrawer
                ? isOpen
                  ? 'Close navigation'
                  : 'Open navigation'
                : isOpen
                  ? 'Close menu'
                  : 'Open menu'
            }
            aria-expanded={isOpen}
          >
            {isOpen ? <XIcon size={18} /> : <ListIcon size={18} />}
          </button>
          <a className="brand" href={MARKETING_URL}>
            <img src="/icon.svg" alt="" width={22} height={22} />
            <span className="brand-name">scrambl</span>
            <span className="visually-hidden">Scrambl home</span>
          </a>
        </div>
        <nav className="site-nav" aria-label="Scrambl">
          <a className="site-nav-marketing" href={`${MARKETING_URL}/features`}>
            Product
          </a>
          <a
            className="site-nav-marketing"
            href={`${MARKETING_URL}/integrations`}
          >
            Integrations
          </a>
          <a className="site-nav-marketing" href={`${MARKETING_URL}/pricing`}>
            Pricing
          </a>
          <Link to="/" aria-current="page">
            Help center
          </Link>
          <a href={STATUS_URL}>Status</a>
        </nav>
        {!isHome ? (
          <div className="site-header-search">
            <SearchBox compact />
          </div>
        ) : null}
        <div className="header-actions">
          <a className="header-login" href={`${APP_URL}/login`}>
            Log in
          </a>
          <a className="header-cta" href={`${APP_URL}/register`}>
            Start free trial
          </a>
        </div>
      </div>
      {!hasDrawer && isMenuOpen ? (
        <nav className="site-menu" aria-label="Scrambl">
          <SiteLinks onNavigate={() => setIsMenuOpen(false)} />
        </nav>
      ) : null}
    </header>
  )
}
